import {
    Anchor,
    Box,
    Button,
    Divider,
    Group,
    Image,
    MantineStyleProps,
    Overlay,
    Radio,
    rem,
    Stack,
    Text,
    TextInput,
    Title,
} from "@mantine/core";
import { useField, useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { createFileRoute, Link } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { useEffect } from "react";
import { useCustomer } from "shared/graphql/cust";
import { features } from "shared/graphql/features";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { Cm } from "src/gql/graphql";
import z from "zod";

const VerifyPanel = (props: { type: Cm; value: string; onVerify: (userId: string) => unknown }) => {
    const verifyField = useField({ initialValue: "", validate: (v) => (v.trim().length < 1 ? "Enter a code" : null) });
    const { data: customer } = useCustomer();
    features.useLoader();

    const sendCode = useGraphqlMutation(muts.sendCode);

    const verifyCode = useGraphqlMutation(
        graphql(`
            mutation OptInVerifyEudLoginCode($type: CM!, $value: String!, $code: String!, $customerId: UUID!) {
                verifyEudLoginCode(params: { type: $type, value: $value, verificationCode: $code, customerId: $customerId }) {
                    id
                }
            }
        `),
    );

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                verifyField
                    .validate()
                    .then(() => verifyCode.mutateAsync({ ...props, code: verifyField.getValue(), customerId: customer.id }))
                    .then((res) => {
                        modals.closeAll();
                        props.onVerify(res.verifyEudLoginCode.id);
                    })
                    .catch(notify.catch);
            }}
        >
            <Stack>
                <Text>
                    A verification code was sent to <em>{props.value.replace(";", " ext. ").replaceAll("W", "")}</em> if it is linked to an
                    existing account.
                </Text>
                This code is only active for the next 15 minutes.
                <Text></Text>
                <TextInput label="Enter Code" required placeholder="Enter code..." {...verifyField.getInputProps()} />
                <Button
                    variant="default"
                    w="100%"
                    onClick={(e) => {
                        e.preventDefault();
                        sendCode
                            .mutateAsync({ type: props.type, value: props.value, customerId: customer.id })
                            .then(() => notify.show.success("Re-sent verification code"))
                            .catch(notify.catch);
                    }}
                >
                    Resend Code
                </Button>
                <Button w="100%" type="submit">
                    Confirm
                </Button>
            </Stack>
        </form>
    );
};

const loginFormSchema = z.object({
    type: z.nativeEnum(Cm),
    value: z.string().nonempty("Required"),
});

const Login = () => {
    const nav = Route.useNavigate();
    const form = useForm({ initialValues: { type: Cm.Email, value: "", code: "" }, validate: zodResolver(loginFormSchema) });
    const formValues = form.getValues();
    const { data: customer } = useCustomer();

    const sendCode = useGraphqlMutation(muts.sendCode);

    useEffect(() => {
        form.clearErrors();
    }, [formValues.type]);

    return (
        <form
            onSubmit={form.onSubmit(() => {
                sendCode
                    .mutateAsync({ type: formValues.type, value: formValues.value, customerId: customer.id })
                    .then(() =>
                        modals.open({
                            withCloseButton: false,
                            children: (
                                <VerifyPanel
                                    type={formValues.type}
                                    value={formValues.value}
                                    onVerify={(userId) => nav({ to: "/opt-in/$userId", params: { userId } }).catch(notify.catch)}
                                />
                            ),
                        }),
                    )
                    .catch(notify.catch);
            })}
        >
            <Stack px={rem(72)}>
                <Radio.Group label="Sign In With:" {...form.getInputProps("type")}>
                    <Group mt="xs">
                        <Radio value={Cm.Email} label="Email" />
                        <Radio value={Cm.Sms} label="Phone Number" />
                    </Group>
                </Radio.Group>
                {formValues.type == Cm.Email ? (
                    <TextInput label="Email" type="email" placeholder="Enter email" {...form.getInputProps("value")} />
                ) : (
                    <TextInput label="Phone Number" type="tel" placeholder="Enter phone number" {...form.getInputProps("value")} />
                )}
                <Button type="submit" w="100%" variant="default">
                    Verify
                </Button>
            </Stack>
        </form>
    );
};

const SignUp = (props: MantineStyleProps) => {
    const { data: customer } = useCustomer();

    return (
        <Stack p={rem(72)} pb={0} justify="center" bdrs={4} style={{ backgroundColor: "var(--mantine-color-body)" }} {...props}>
            <Image
                mx="auto"
                src={`${import.meta.env.VITE_API_BASE_URL}/customers/${customer.id}/icon`}
                fallbackSrc={"/bryx.svg"}
                radius="md"
                style={{ maxWidth: rem(100), maxHeight: rem(100) }}
            />
            <div style={{ textAlign: "center" }}>
                <Title order={5}>{customer.name}</Title>
                <Title order={6} c="dimmed">
                    Mass Notification System
                </Title>
            </div>
            <Link to="/opt-in/register">
                <Button variant="filled" w="100%" type="submit">
                    Sign Up
                </Button>
            </Link>
            <Divider />
            <Text fz="sm" mx="auto">
                Already Signed Up?
            </Text>
        </Stack>
    );
};

const Footer = () => (
    <div>
        <Divider />
        <Box py="xl" bg="gray-accent" ta="center" style={{ borderRadius: "4px" }}>
            <Text size="sm">
                Powered by{" "}
                <Anchor fw="bold" td="none" href="https://bryx.com/bryx-mns">
                    Bryx Alerts
                </Anchor>
            </Text>
        </Box>
    </div>
);

const Page = () => {
    features.useGuard("HAS_WEB_OPT_IN");
    const isMobile = useIsMobile();

    if (isMobile)
        return (
            <Stack justify="space-between" h="100vh">
                <Stack my="auto">
                    <SignUp />
                    <Login />
                </Stack>
                <Footer />
            </Stack>
        );

    return (
        <Group h="fit-content" mih="100vh" w="100vw" bg="brand" pos="relative">
            <Overlay gradient="linear-gradient(145deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0) 100%)" opacity={0.85} />
            <Stack justify="space-between" h="100vh" bg="brand">
                <Stack
                    flex={1}
                    justify="space-between"
                    m="xs"
                    style={{ backgroundColor: "var(--mantine-color-body)", borderRadius: "4px", zIndex: 201 }}
                >
                    <Stack my="auto">
                        <SignUp w={500} />
                        <Login />
                    </Stack>
                    <Footer />
                </Stack>
            </Stack>
        </Group>
    );
};

const muts = {
    sendCode: graphql(`
        mutation OptInSendEudLoginCode($type: CM!, $value: String!, $customerId: UUID!) {
            sendEudLoginCode(params: { type: $type, value: $value, customerId: $customerId })
        }
    `),
};

/** @public */
export const Route = createFileRoute("/opt-in/")({
    component: Page,
    async beforeLoad() {
        await features.load();
    },
});
